import React, { useEffect, useRef, useState, useCallback } from "react";

import LevelsChart from "../components/LevelsChart";
import API_BASE_URL from "../api";
import { authFetch } from "../authFetch";
import { useAuth } from "../authContext";
import { useTranslation } from "react-i18next";

export default function SilenceLevelsPage() {
  // авторизация
  const { isAuth } = useAuth();
  const { t } = useTranslation("common");

  // список стримов пользователя
  const [streams, setStreams] = useState([]);
  const [loadingStreams, setLoadingStreams] = useState(true);

  // выбранный стрим
  const [selectedId, setSelectedId] = useState("");

  // ===== Даты =====
  const pad2 = (n) => String(n).padStart(2, "0");
  const toYMD = (d) =>
    `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  const [selectedDate, setSelectedDate] = useState(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return toYMD(d);
  });

  // режимы (сутки / час / минута)
  const [selectedHour, setSelectedHour] = useState("");
  const [selectedMinute, setSelectedMinute] = useState("");

  function clampToLast7Days(dateISO) {
    const d = new Date(`${dateISO}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sevenAgo = new Date(today.getTime() - 6 * 86400000);
    if (d < sevenAgo) return toYMD(sevenAgo);
    if (d > today) return toYMD(today);
    return toYMD(d);
  }

  const last7Dates = (() => {
    const arr = [];
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    for (let i = 0; i < 7; i++) {
      const d = new Date(base.getTime() - i * 86400000);
      arr.push(toYMD(d));
    }
    return arr;
  })();

  // ===== Данные графика =====
  const [minutes, setMinutes] = useState([]);
  const [seconds, setSeconds] = useState([]);
  const [chartLoading, setChartLoading] = useState(false);
  const [chartError, setChartError] = useState(null);
  const [chartKey, setChartKey] = useState("");

  // Контроль гонок
  const requestSeq = useRef(0);
  const controllerRef = useRef(null);

  // загрузка стримов
  const fetchStreams = useCallback(async () => {
    setLoadingStreams(true);
    try {
      if (!isAuth) {
        setStreams([]);
        return;
      }
      const API = (API_BASE_URL || "").replace(/\/+$/, "");
      const res = await authFetch(
        API + "/streams"
      );
      if (res.status === 401) {
        setStreams([]);
      } else if (res.ok) {
        const data = await res.json();
        setStreams(Array.isArray(data) ? data : []);
      } else {
        setStreams([]);
      }
    } catch {
      setStreams([]);
    } finally {
      setLoadingStreams(false);
    }
  }, [isAuth]);


  useEffect(() => {
    if (isAuth) {
      fetchStreams();
    } else {
      setStreams([]);
      setLoadingStreams(false);
    }
  }, [isAuth, fetchStreams]);


  const activeStreams = streams.filter((s) => s?.is_active);

  // ===== Построение диапазона =====
  function buildRange() {
    const tz =
      Intl.DateTimeFormat().resolvedOptions().timeZone || "local";
    const dateISO = clampToLast7Days(selectedDate || toYMD(new Date()));

    // СУТКИ: 00:00:00 — 23:59:59 выбранной даты, шаг минуты
    if (selectedHour === "") {
      const from = new Date(`${dateISO}T00:00:00`);
      const to = new Date(`${dateISO}T23:59:59`);
      const labelFrom = `${toYMD(from)} 00:00`;
      const labelTo = `${toYMD(to)} 23:59`;
      return { from, to, step: 60, labelFrom, labelTo, tz };
    }

    // ЧАС или МИНУТА — от выбранной даты и часа
    const hour = Number(selectedHour);
    const hourBase = new Date(`${dateISO}T${pad2(hour)}:00:00`);

    // ЧАС: H:00:00 — H:59:59, шаг минуты
    if (selectedMinute === "") {
      const from = new Date(hourBase);
      const to = new Date(`${dateISO}T${pad2(hour)}:59:59`);
      const labelFrom = `${toYMD(from)} ${pad2(from.getHours())}:00`;
      const labelTo = `${toYMD(to)} ${pad2(to.getHours())}:${pad2(
        to.getMinutes()
      )}`;
      return { from, to, step: 60, labelFrom, labelTo, tz };
    }

    // МИНУТА: H:M:00 — H:M:59, шаг секунды
    const minute = Number(selectedMinute);
    const from = new Date(`${dateISO}T${pad2(hour)}:${pad2(minute)}:00`);
    const to = new Date(`${dateISO}T${pad2(hour)}:${pad2(minute)}:59`);
    const labelFrom = `${toYMD(from)} ${pad2(from.getHours())}:${pad2(
      from.getMinutes()
    )}`;
    const labelTo = `${toYMD(to)} ${pad2(to.getHours())}:${pad2(
      to.getMinutes()
    )}`;
    return { from, to, step: 1, labelFrom, labelTo, tz };
  }

  // авто-выбор первого активного
  useEffect(() => {
    if (!isAuth || loadingStreams || selectedId) return;
    const first = streams.find((s) => s?.is_active);
    if (first) setSelectedId(String(first.id));
  }, [isAuth, loadingStreams, selectedId, streams]);

  // единый фетч
  useEffect(() => {
    if (!isAuth || !selectedId) return;

    if (controllerRef.current) controllerRef.current.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const signal = controller.signal;
    const mySeq = ++requestSeq.current;

    const run = async () => {
      setChartError(null);
      setChartLoading(true);
      setMinutes([]);
      setSeconds([]);

      const base = (API_BASE_URL || "").replace(/\/+$/, "");
      const { from, to, step } = buildRange();



      const qs = new URLSearchParams({
        stream_id: String(selectedId),
        date_from: from.toISOString(), // ВАЖНО: backend ждёт date_from/date_to
        date_to: to.toISOString(),
        step_secs: String(step),
        _: String(Date.now()),
      }).toString();

      const url = `${base}/silence/levels?${qs}`;

      try {
        const res = await authFetch(url, {
          signal,
          cache: "no-store",
        });
        if (signal.aborted) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();
        if (mySeq !== requestSeq.current) return;

        const minsRaw = Array.isArray(data?.minutes)
          ? data.minutes
          : Array.isArray(data)
          ? data
          : [];
        const mins = minsRaw
          .map((d) => {
            const t = d.t_local || d.ts_minute || d.ts || d.time || null;
            const v = Number(
              d.avg_dbfs ?? d.dbfs_avg ?? d.level ?? d.dbfs ?? null
            );
            return t != null && Number.isFinite(v)
              ? { ts: String(t).replace(" ", "T"), level: v }
              : null;
          })
          .filter(Boolean);

        const secsRaw = Array.isArray(data?.seconds) ? data.seconds : [];
        const secs = secsRaw
          .map((d) => {
            const t = d.t_local || d.ts || d.time || null;
            const v = Number(d.avg_dbfs ?? d.dbfs ?? d.level ?? null);
            return t != null && Number.isFinite(v)
              ? { ts: String(t).replace(" ", "T"), level: v }
              : null;
          })
          .filter(Boolean);

        setMinutes(mins);
        setSeconds(secs);

        setChartKey(
          `${selectedId}-${from.toISOString()}-${to.toISOString()}-${step}-${mySeq}`
        );
      } catch (e) {
        if (e.name !== "AbortError")
          setChartError(e.message || t("levels.chart.load_error"));
      } finally {
        if (mySeq === requestSeq.current) setChartLoading(false);
      }
    };

    run();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth, selectedId, selectedDate, selectedHour, selectedMinute]);

  useEffect(() => () => controllerRef.current?.abort(), []);

  // выбор minutes/seconds
  const useMinutes = selectedHour === "";
  const levels = useMinutes ? minutes : seconds;

  // фиксированные границы оси X для отрисовки
  const dayStart = new Date(`${selectedDate}T00:00:00`);
  const dayEnd = new Date(`${selectedDate}T23:59:59`);

  let xMin = dayStart;
  let xMax = dayEnd;
  let timeUnit = "minute"; // для суток — минуты

  if (!useMinutes) {
    const h = Number(selectedHour || 0);
    const m = selectedMinute === "" ? 0 : Number(selectedMinute);

    if (selectedMinute === "") {
      // выбран час: H:00:00 — H:59:59
      xMin = new Date(`${selectedDate}T${pad2(h)}:00:00`);
      xMax = new Date(`${selectedDate}T${pad2(h)}:59:59`);
      timeUnit = "second";
    } else {
      // выбраны час и минута: H:M:00 — H:M:59
      xMin = new Date(`${selectedDate}T${pad2(h)}:${pad2(m)}:00`);
      xMax = new Date(`${selectedDate}T${pad2(h)}:${pad2(m)}:59`);
      timeUnit = "second";
    }
  }

  return (
    <div style={{ maxWidth: 1050, margin: "48px auto" }}>
      <h2 style={{ fontWeight: 800, fontSize: 30, marginBottom: 22 }}>
        {t("levels.title")}
      </h2>

      {!isAuth && (
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: 24,
            boxShadow: "0 2px 12px #0001",
            color: "#444",
          }}
        >
          {t("levels.login_hint")}
        </div>
      )}

      {isAuth && (
        <>
          {/* выбор даты / час / минута */}
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              marginBottom: 12,
            }}
          >
            <select
              value={selectedDate}
              onChange={(e) => {
                setSelectedDate(e.target.value);
              }}
              style={{ padding: "8px 10px", fontSize: 14 }}
            >
              {last7Dates.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </select>

            <select
              value={selectedHour}
              onChange={(e) => {
                setSelectedHour(e.target.value);
                setSelectedMinute("");
              }}
              style={{ padding: "8px 10px", fontSize: 14 }}
            >
              <option value="">{t("levels.range.day")}</option>
              {Array.from({ length: 24 }, (_, h) => h).map((h) => (
                <option key={h} value={String(h)}>
                  {String(h).padStart(2, "0")}:00
                </option>
              ))}
            </select>

            <select
              value={selectedMinute}
              onChange={(e) => {
                setSelectedMinute(e.target.value);
              }}
              disabled={selectedHour === ""}
              style={{ padding: "8px 10px", fontSize: 14 }}
            >
              <option value="">{t("levels.range.full_hour")}</option>
              {Array.from({ length: 60 }, (_, m) => m).map((m) => (
                <option key={m} value={String(m)}>
                  {String(m).padStart(2, "0")}
                </option>
              ))}
            </select>
          </div>

          {/* стрим */}
          <div style={{ marginBottom: 16 }}>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              disabled={loadingStreams || activeStreams.length === 0}
              style={{ padding: "10px 12px", fontSize: 16, minWidth: 320 }}
            >
              <option value="">
                {loadingStreams
                  ? t("levels.streams.loading")
                  : activeStreams.length === 0
                  ? t("levels.streams.none")
                  : t("levels.streams.choose")}
              </option>
              {activeStreams.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || t("levels.streams.fallback", { id: s.id })}
                </option>
              ))}
            </select>
          </div>

          {/* график */}
          {selectedId ? (
            chartError ? (
              <div
                style={{
                  background: "#ffe8e8",
                  borderRadius: 12,
                  padding: 14,
                  color: "#900",
                }}
              >
                {t("levels.chart.error_prefix")} {chartError}
              </div>
            ) : chartLoading ? (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 12,
                  padding: 14,
                  color: "#666",
                  boxShadow: "0 2px 12px #0001",
                }}
              >
                {t("common.loading")}
              </div>
            ) : (
              <LevelsChart
                key={chartKey}
                data={levels}
                xMin={xMin}
                xMax={xMax}
                timeUnit={timeUnit}
              />
            )
          ) : (
            <div
              style={{
                background: "#fff",
                borderRadius: 12,
                padding: 24,
                boxShadow: "0 2px 12px #0001",
                color: "#666",
              }}
            >
              {t("levels.chart.select_prompt")}
            </div>
          )}
        </>
      )}
    </div>
  );
}
